/**
 * GeneradorPdf - adaptada para jsPDF 2.x + jspdf-autotable 3.x
 * Incluye shims de compatibilidad para setFontStyle / setFontType.
 **/

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { svgAsPngUri } from "save-svg-as-png";

class GeneradorPdf {
    constructor(orientacion, tamagno) {
        this._doc = null;
        this._empresa = null;
        this.crear(orientacion, tamagno);
    }

    get doc() { return this._doc; }
    set doc(value) { this._doc = value; }

    crear(orientacion, tamagno) {
        const orientacionReal = orientacion ? orientacion : "p";
        const tamagnoReal = tamagno ? tamagno : "a4";

        this._doc = new jsPDF(orientacionReal, "pt", tamagnoReal);

        // --- SHIMS de compatibilidad (para evitar errores por código/plug-ins antiguos) ---
        // 1) Shim en la instancia
        if (typeof this._doc.setFontStyle !== "function") {
            this._doc.setFontStyle = (style) => {
                try {
                    // Intentar preservar el nombre de fuente actual si es posible
                    let fontName = "helvetica";
                    if (typeof this._doc.getFont === "function") {
                        const gf = this._doc.getFont();
                        if (gf) fontName = gf.fontName || gf.fontFamily || gf.font || fontName;
                    }
                    this._doc.setFont(fontName, style);
                } catch (e) {
                    // Fallback muy conservador
                    try { this._doc.setFont(undefined, style); } catch (e2) { /* no-op */ }
                }
            };
        }
        if (typeof this._doc.setFontType !== "function") {
            this._doc.setFontType = this._doc.setFontStyle;
        }

        // 2) Shim en el prototype de jsPDF (para cubrir llamadas que usen la API global)
        if (jsPDF && jsPDF.API && typeof jsPDF.API.setFontStyle !== "function") {
            jsPDF.API.setFontStyle = function(style) {
                try {
                    let fontName = "helvetica";
                    if (typeof this.getFont === "function") {
                        const gf = this.getFont();
                        if (gf) fontName = gf.fontName || gf.fontFamily || gf.font || fontName;
                    }
                    this.setFont(fontName, style);
                } catch (e) {
                    try { this.setFont(undefined, style); } catch (e2) { /* no-op */ }
                }
            };
        }
        if (jsPDF && jsPDF.API && typeof jsPDF.API.setFontType !== "function") {
            jsPDF.API.setFontType = jsPDF.API.setFontStyle;
        }
        // --- FIN SHIMS ---
    }

    guardar(nombre) {
        if (this._doc) this._doc.save(nombre);
    }

    setFuente(fuente) {
        this._doc.setFont(fuente);
    }

    addImagen(dataUrl, tipo, x, y, width, height) {
        if (width != null && height != null) this._doc.addImage(dataUrl, tipo, x, y, width, height);
        else this._doc.addImage(dataUrl, tipo, x, y);
    }

    addText(x, y, texto, fuenteTamagno, opciones = {}, fuenteNombre) {
        if (fuenteNombre) this._doc.setFont(fuenteNombre);
        if (fuenteTamagno && fuenteTamagno > 0) this._doc.setFontSize(fuenteTamagno);

        let xReal = x;
        if (opciones.align === "center") {
            const textWidth = this._doc.getStringUnitWidth(texto) * this._doc.internal.getFontSize() / this._doc.internal.scaleFactor;
            xReal = (this._doc.internal.pageSize.width - textWidth) / 2;
        }

        this._doc.text(texto, xReal, y, opciones);
    }

    addTable(x, y, datos, pie, columnas, estilosColumnas, stripped, onWillDrawCell, onDidParseCell, tamagnoLetraHead=12, tamagnoLetraBody= 10) {
        let newEstilosColumnas = this.transformarColumnsWidths(estilosColumnas);

        let anchoTotal = 0;
        for (const prop in newEstilosColumnas) anchoTotal += newEstilosColumnas[prop].cellWidth;
        let pageWidth = this._doc.internal.pageSize.width;
        let margen = (pageWidth - anchoTotal) / 2;

        let onDidDrawPage = (data) => {
            data.doc.setFontSize(8);
            let str = "Pag. " + data.pageNumber;
            this.addText(0, data.doc.internal.pageSize.height - 20, str, 8, { align: "center" });
        };

        let didParseCell = (data) => {
            let seccion = data.cell.section;
            let row = data.row.index;
            if (Boolean(stripped) && seccion === "body") {
                if (row % 2 === 0) data.cell.styles.fillColor = [240, 240, 240];
            }
            onDidParseCell && onDidParseCell(data);
        };

        let willDrawCell = (data) => { onWillDrawCell && onWillDrawCell(data); };

        autoTable(this._doc, {
            startY: y,
            columnStyles: newEstilosColumnas,
            head: [columnas],
            body: datos,
            foot: pie ? [pie] : null,
            margin: { left: margen, right: margen },
            showFoot: "lastPage",
            didDrawPage: onDidDrawPage,
            didParseCell: didParseCell,
            willDrawCell: willDrawCell,
            theme: "plain",
            bodyStyles: { fontSize: tamagnoLetraBody, cellPadding: 3, fontStyle: "normal" },
            headStyles: { fontSize: tamagnoLetraHead, fillColor: "gray", textColor: "white", fontStyle: "bold" }
        });
    }

    addSvg(svg, opciones, callback) {
        let doc = this._doc;
        let conversorWidth = this.getConversorWidth();

        svgAsPngUri(svg, { scale: opciones.scale, backgroundColor: opciones.backgroundColor }).then(uri => {
            let image = document.createElement("img");
            image.src = uri;
            image.onload = function () {
                let canvas = document.createElement("canvas");
                canvas.width = image.width;
                canvas.height = image.height;
                let context = canvas.getContext("2d");
                context.drawImage(image, 0, 0, image.width, image.height);
                let dataUrl = canvas.toDataURL("image/jpeg");

                let xReal = opciones.x;
                if (opciones.align === "center") {
                    let imageWidthModificada = image.width;
                    xReal = (doc.internal.pageSize.width - imageWidthModificada) / 2;
                }
                doc.addImage(dataUrl, "PNG", xReal, opciones.y);
                callback && callback(canvas.width, canvas.height);
            };
        });
    }

    getConversorHeight() { return 0.75; }
    getConversorWidth() { return 0.75; }

    getTextWidth(texto) {
        return this._doc.getStringUnitWidth(texto) * this._doc.internal.getFontSize() / this._doc.internal.scaleFactor;
    }

    getPageSize() { return this._doc.internal.pageSize; }

    transformarColumnsWidths(estilosColumnas) {
        let tablaMargen = 20;
        let anchoTotal = 0;
        for (const prop in estilosColumnas) anchoTotal += estilosColumnas[prop].cellWidth;
        let pageWidth = this._doc.internal.pageSize.width;
        let ratio = (pageWidth - 2 * tablaMargen) / anchoTotal;

        let newEstilosColumnas = { ...estilosColumnas };
        for (const prop in newEstilosColumnas) {
            let cellWidth = newEstilosColumnas[prop].cellWidth;
            newEstilosColumnas[prop].cellWidth = Math.trunc(cellWidth * ratio);
        }
        return newEstilosColumnas;
    }

    hexToRgb(hex) {
        var bigint = parseInt(hex.replace("#", ""), 16);
        var r = (bigint >> 16) & 255;
        var g = (bigint >> 8) & 255;
        var b = bigint & 255;
        return [r, g, b];
    }
}

export default GeneradorPdf;
